import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WeekSelectorComponent } from '../week-selector/week-selector.component';
import { CallCenterStatsTableComponent } from '../call-center-stats-table/call-center-stats-table.component';
import { AuthService } from '../../../services/auth.service';
import { CallCenterStatsService } from '../../../services/call-center-stats.service';
import { DateService } from '../../../services/date.service';
import { WeekInfo, CallCenterStatsData } from '../../../interfaces/call-center-stats.interface';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-call-center-stats-wrapper',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    WeekSelectorComponent,
    CallCenterStatsTableComponent
],
  templateUrl: './call-center-stats-wrapper.component.html',
  styleUrls: ['./call-center-stats-wrapper.component.scss']
})
export class CallCenterStatsWrapperComponent implements OnInit, OnDestroy {
  selectedWeek: WeekInfo | null = null;
  callCenterUsers: any[] = [];
  usersWithHistoricalData: number[] = [];
  isLoading: boolean = true;
  isExporting: boolean = false;

  // Előre betöltött heti statisztikák userId kulccsal (bulk endpoint-ból)
  // null = még nem töltődött be; {} = betöltődött (de nincs adat)
  preloadedWeekStats: { [userId: number]: CallCenterStatsData } | null = null;

  // Gyorsítótárazott tömb az összesítő táblához (elkerüli az NG0100 hibát)
  allPreloadedStatsArray: CallCenterStatsData[] | null = null;

  // Inflight kérések megszakításához
  private readonly cancelRequest$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private callCenterStatsService: CallCenterStatsService,
    private dateService: DateService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.loadDataForCurrentMonth();
  }

  ngOnDestroy(): void {
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDataForCurrentMonth() {
    this.isLoading = true;

    const currentDate = this.dateService.getCurrentDate();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1; // JavaScript months are 0-based

    // Jelenlegi felhasználó
    const currentUser = this.authService.getCurrentUser();
    
    // Ha Call Center munkatárs, csak a saját statisztikákat jelenítjük meg
    if (this.authService.isCallCenterEmployee() && currentUser) {
      this.callCenterUsers = [currentUser];
      this.usersWithHistoricalData = [currentUser.id!];
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Admin vagy vezetői szerepkör esetén az összes Call Center munkatárs statisztikái
    // Párhuzamosan töltjük be a usereket és a történelmi adatokat
    forkJoin({
      users: this.authService.getAllUsers(),
      historicalUsers: this.callCenterStatsService.getUsersWithHistoricalData(year, month)
    }).pipe(takeUntil(this.cancelRequest$)).subscribe({
      next: (result) => {
        // Call Center munkatársakat szűrjük ki
        const allCallCenterUsers = result.users.filter(user => {
          return user.jobs?.includes('call_center') || user.username === 'PSándor';
        });

        // Hibrid logika: mutassuk meg a usereket
        this.callCenterUsers = allCallCenterUsers.filter(user => {
          return this.shouldShowUserTable(user, result.historicalUsers, currentDate);
        });

        this.usersWithHistoricalData = result.historicalUsers;

        // Bulk betöltés - isLoading=false a loadBulkWeekStats-ban
        this.loadBulkWeekStats();
      },
      error: (error) => {
        this.callCenterUsers = [];
        this.usersWithHistoricalData = [];
        this.isLoading = false;
      }
    });
  }

  shouldShowUserTable(user: any, usersWithHistoricalData: number[], currentDate: Date): boolean {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    
    // Meghatározzuk, hogy ez történelmi hónap-e (múltbeli)
    const isHistoricalMonth = this.isHistoricalMonth(year, month);
    
    if (isHistoricalMonth) {
      if (!user.is_active) {
        // Inaktív user: csak akkor jelenik meg, ha van adata a hónapban
        return usersWithHistoricalData.includes(user.id);
      }
      // Aktív user: csak a létrehozás hónapjától jelenjen meg
      return this.isMonthOnOrAfterCreatedAt(year, month, user.created_at);
    } else {
      // Jelenlegi/jövőbeli hónapokhoz: aktív user, VAGY inaktív user aki már van adata az adott hónapban
      return user.is_active === true || usersWithHistoricalData.includes(user.id);
    }
  }

  private isMonthOnOrAfterCreatedAt(year: number, month: number, createdAt: string | null | undefined): boolean {
    if (!createdAt) return true;
    const created = new Date(createdAt);
    const createdYear = created.getFullYear();
    const createdMonth = created.getMonth() + 1;
    return year > createdYear || (year === createdYear && month >= createdMonth);
  }

  isHistoricalMonth(year: number, month: number): boolean {
    const currentDate = this.dateService.getCurrentDate();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    // Ha korábbi év, vagy ugyanazon évben korábbi hónap
    return year < currentYear || (year === currentYear && month < currentMonth);
  }

  loadCallCenterUsers() {
    // Ez a metódus most átirányít az új hibrid metódusra
    this.loadDataForCurrentMonth();
  }

  onWeekChanged(weekInfo: WeekInfo) {
    this.cancelRequest$.next(); // előző inflight kérések megszakítása
    this.selectedWeek = weekInfo;
    this.loadDataForCurrentWeek(weekInfo);
  }

  loadDataForCurrentWeek(weekInfo: WeekInfo) {
    this.isLoading = true;

    const year = weekInfo.startDate.getFullYear();
    const month = weekInfo.startDate.getMonth() + 1;

    // Jelenlegi felhasználó
    const currentUser = this.authService.getCurrentUser();
    
    // Ha Call Center munkatárs, csak a saját statisztikákat jelenítjük meg
    if (this.authService.isCallCenterEmployee() && currentUser) {
      this.callCenterUsers = [currentUser];
      this.usersWithHistoricalData = [currentUser.id!];
      // Bulk betöltés - isLoading=false a loadBulkWeekStats-ban
      this.loadBulkWeekStats();
      return;
    }

    // Admin vagy vezetői szerepkör esetén az összes Call Center munkatárs statisztikái
    // Párhuzamosan töltjük be a usereket és a történelmi adatokat
    forkJoin({
      users: this.authService.getAllUsers(),
      historicalUsers: this.callCenterStatsService.getUsersWithHistoricalData(year, month)
    }).pipe(takeUntil(this.cancelRequest$)).subscribe({
      next: (result) => {
        // Call Center munkatársakat szűrjük ki
        const allCallCenterUsers = result.users.filter(user => {
          return user.jobs?.includes('call_center') || user.username === 'PSándor';
        });

        // Hibrid logika: mutassuk meg a usereket
        this.callCenterUsers = allCallCenterUsers.filter(user => {
          return this.shouldShowUserTable(user, result.historicalUsers, weekInfo.startDate);
        });

        this.usersWithHistoricalData = result.historicalUsers;

        // Bulk betöltés - isLoading=false a loadBulkWeekStats-ban
        this.loadBulkWeekStats();
      },
      error: (error) => {
        this.callCenterUsers = [];
        this.usersWithHistoricalData = [];
        this.isLoading = false;
      }
    });
  }

  getSeasonName(season: string): string {
    switch (season) {
      case 'spring': return 'Tavasz';
      case 'summer': return 'Nyár';
      case 'autumn': return 'Ősz';
      case 'winter': return 'Tél';
      default: return '';
    }
  }

  /**
   * Ellenőrzi, hogy a felhasználó adminisztrátor-e
   */
  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  /**
   * Ellenőrzi, hogy a felhasználó vezetői szerepkörrel rendelkezik-e
   */
  isManager(): boolean {
    return this.authService.isManager();
  }

  /**
   * Ellenőrzi, hogy a felhasználó Call Center munkatárs-e
   */
  isCallCenterEmployee(): boolean {
    return this.authService.isCallCenterEmployee();
  }

  /**
   * Visszaadja a jelenlegi felhasználót
   */
  getCurrentUser(): any {
    return this.authService.getCurrentUser();
  }

  /**
   * Bulk heti statisztikák betöltése az összes CC munkatárshoz egyetlen API kéréssel.
   * Az eredmény preloadedWeekStats-ba kerül, amit a template továbbít a gyerekeknek.
   */
  private loadBulkWeekStats(): void {
    const userIds = this.callCenterUsers.map((u: any) => u.id).filter((id: number) => id > 0);

    if (userIds.length === 0) {
      this.preloadedWeekStats = {};
      this.allPreloadedStatsArray = null;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    let weekNumber: number;
    let year: number;

    if (this.selectedWeek) {
      weekNumber = this.selectedWeek.weekNumber;
      year = this.selectedWeek.startDate.getFullYear();
    } else {
      const weekInfo = this.callCenterStatsService.getCurrentWeekInfo();
      weekNumber = weekInfo.weekNumber;
      year = weekInfo.year;
    }

    this.callCenterStatsService.getBulkStats(userIds, [weekNumber], year).pipe(takeUntil(this.cancelRequest$)).subscribe({
      next: (allStats) => {
        const statsMap: { [userId: number]: CallCenterStatsData } = {};
        allStats.forEach(stat => {
          statsMap[stat.user_id] = stat;
        });
        this.preloadedWeekStats = statsMap;
        const vals = Object.values(statsMap);
        this.allPreloadedStatsArray = vals.length > 0 ? vals : null;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba a bulk heti statisztikák betöltésekor:', error);
        this.preloadedWeekStats = {};
        this.allPreloadedStatsArray = null;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Összesítő táblához: az összes előre betöltött statisztika tömbként
   * @deprecated Használd az allPreloadedStatsArray property-t
   */
  getAllPreloadedStatsArray(): CallCenterStatsData[] | null {
    return this.allPreloadedStatsArray;
  }

  isExportDisabled(): boolean {
    return this.isLoading || this.callCenterUsers.length === 0 || this.preloadedWeekStats === null;
  }

  onExportRequested(): void {
    if (!this.selectedWeek || this.isExportDisabled()) {
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    const weekNumber = this.selectedWeek.weekNumber;
    const year = this.selectedWeek.startDate.getFullYear();
    const userIds = this.callCenterUsers.map((user: any) => user.id).filter((id: number) => id > 0);

    // Szándékosan csak a destroy$ szakítja meg: hétváltásnál se szakadjon félbe a letöltés
    this.callCenterStatsService.exportWeekStats(userIds, weekNumber, year)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.downloadReportFile(blob, weekNumber, year);
          this.isExporting = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.snackBar.open('A heti jelentés exportálása sikertelen', 'OK', { duration: 4000 });
          this.isExporting = false;
          this.cdr.detectChanges();
        }
      });
  }

  private downloadReportFile(blob: Blob, weekNumber: number, year: number): void {
    const fileName = `Heti_jelentes_${year}_${String(weekNumber).padStart(2, '0')}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
